import React, { useEffect, useMemo, useRef, useState } from "react";
import { ALL_COLUMNS } from "./columns.config";
import { FiColumns, FiSearch, FiX } from "react-icons/fi";

export default function ColumnPicker({
  visibleIds,
  setVisibleIds,
  onReset,
  onSelectAll,
  onSelectNone,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const options = useMemo(
    () => ALL_COLUMNS.map((c) => ({ id: c.id, label: c.label })),
    []
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedCount = visibleIds.length;
  const totalCount = options.length;

  const toggle = (id) => {
    setVisibleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Fermer sur clic extérieur
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      const target = e.target;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/95 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 hover:bg-white dark:hover:bg-slate-900 shadow-md ring-1 ring-slate-200/70 dark:ring-slate-700/60 text-sm font-semibold"
        title="Choisir les colonnes"
      >
        <FiColumns className="opacity-80" />
        Colonnes
        <span className="text-xs px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
          {selectedCount}/{totalCount}
        </span>
      </button>

      {open && (
        <div
          ref={panelRef}
          // Responsive: centré sur petits écrans, dropdown à droite sur ≥sm
          className="
            absolute sm:right-0 sm:left-auto sm:translate-x-0
            left-1/2 -translate-x-1/2
            mt-2 w-[min(92vw,22rem)]
            max-h-[70vh] overflow-auto
            rounded-2xl backdrop-blur
            bg-white/95 dark:bg-slate-900/95
            text-slate-900 dark:text-slate-100
            shadow-xl ring-1 ring-slate-200/70 dark:ring-slate-700/60 z-50
          "
        >
          {/* Header */}
          <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 px-3 pt-3 pb-2 border-b border-slate-200/70 dark:border-slate-800/60 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiColumns className="opacity-80" />
                <span className="text-sm font-semibold">Colonnes visibles</span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Fermer"
                title="Fermer"
              >
                <FiX />
              </button>
            </div>

            {/* Actions rapides */}
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={onSelectAll}
                className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
              >
                Tout
              </button>
              <button
                onClick={onSelectNone}
                className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
              >
                Aucun
              </button>
              <button
                onClick={onReset}
                className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
              >
                Réinitialiser
              </button>

              {/* Recherche */}
              <div className="ml-auto relative hidden sm:block">
                <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher…"
                  className="pl-8 pr-2 py-1.5 text-sm rounded-lg bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/60 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Recherche mobile (affichée sous les boutons) */}
            <div className="mt-2 block sm:hidden">
              <div className="relative">
                <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher…"
                  className="w-full pl-8 pr-2 py-2 text-sm rounded-lg bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/60 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>
          </div>

          {/* Liste des options */}
          <div className="p-2">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-6 text-sm text-slate-500 dark:text-slate-400">
                Aucune colonne ne correspond à “{query}”.
              </div>
            ) : (
              <ul className="space-y-0.5">
                {filteredOptions.map((opt) => {
                  const checked = visibleIds.includes(opt.id);
                  return (
                    <li key={opt.id}>
                      <label
                        className="
                          group flex items-center gap-3 w-full
                          px-3 py-2 rounded-xl cursor-pointer
                          hover:bg-slate-50 dark:hover:bg-slate-800/60
                          focus-within:ring-2 focus-within:ring-violet-400
                        "
                      >
                        <input
                          type="checkbox"
                          className="
                            h-4 w-4 rounded
                            border-slate-300 dark:border-slate-600
                            text-violet-600 focus:ring-violet-400
                            bg-white dark:bg-slate-900
                          "
                          checked={checked}
                          onChange={() => toggle(opt.id)}
                        />
                        <span className="text-sm">
                          {opt.label}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 px-3 py-2 border-t border-slate-200/70 dark:border-slate-800/60 bg-white/95 dark:bg-slate-900/95 rounded-b-2xl text-xs text-slate-600 dark:text-slate-400">
            Identité et Actions restent toujours visibles.
          </div>
        </div>
      )}
    </div>
  );
}
